import React, { useState } from 'react';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { sendToWhatsApp } from '../../utils/whatsapp';

// Глобальные переменные для аналитики
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    ym?: (id: number, method: string, goal: string, params?: any) => void;
  }
}

interface LeadFormProps {
  isOpen: boolean;
  onClose: () => void;
  leadType: 'order' | 'callback' | 'consultation' | 'catalog' | 'calculator' | 'certificate' | 'transport';
  productName?: string;
  productId?: string;
  calculatedPrice?: number;
}

interface LeadData {
  name: string;
  phone: string;
  email: string;
  message?: string;
  agreeToDataProcessing: boolean;
}

const leadTypeLabels = {
  order: 'Заказ товара',
  callback: 'Обратный звонок',
  consultation: 'Консультация',
  catalog: 'Запрос прайс-листа',
  calculator: 'Расчет стоимости',
  certificate: 'Информация о сертификате',
  transport: 'Расчет транспорта'
};

const LeadForm: React.FC<LeadFormProps> = ({
  isOpen,
  onClose,
  leadType,
  productName,
  productId,
  calculatedPrice
}) => {
  const [leadData, setLeadData] = useState<LeadData>({
    name: '',
    phone: '',
    email: '',
    message: '',
    agreeToDataProcessing: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (field: keyof LeadData, value: string | boolean) => {
    setLeadData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!leadData.agreeToDataProcessing) {
      alert('Необходимо согласие на обработку персональных данных');
      return;
    }
    
    setIsSubmitting(true);

    // Подготовка данных для отправки
    const leadPayload = {
      type: leadType,
      leadTypeLabel: leadTypeLabels[leadType],
      ...leadData,
      productName,
      productId,
      calculatedPrice,
      source: 'website',
      timestamp: new Date().toISOString(),
      page: window.location.pathname
    };

    try {
      // WhatsApp-қа жіберу
      const message = `${leadTypeLabels[leadType]}${productName ? `\nТовар: ${productName}` : ''}${calculatedPrice ? `\nРасчет: ${calculatedPrice} тг` : ''}\nИмя: ${leadData.name}\nТелефон: ${leadData.phone}${leadData.email ? `\nEmail: ${leadData.email}` : ''}${leadData.message ? `\nКомментарий: ${leadData.message}` : ''}`;
      sendToWhatsApp(message, leadTypeLabels[leadType]);

      // Отправка в Google Analytics
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'generate_lead', {
          currency: 'KZT',
          value: calculatedPrice || 0,
          lead_type: leadType,
          product_name: productName
        });
      }

      // Отправка в Yandex.Metrica
      if (typeof window.ym !== 'undefined') {
        window.ym(12345678, 'reachGoal', `lead_${leadType}`, {
          product_name: productName,
          price: calculatedPrice
        });
      }

      setIsSuccess(true);
      
      // Очистка формы через 3 секунды
      setTimeout(() => {
        setIsSuccess(false);
        setLeadData({
          name: '',
          phone: '',
          email: '',
          message: '',
          agreeToDataProcessing: false
        });
        onClose();
      }, 3000);

    } catch (error) {
      console.error('Ошибка отправки лида:', error);
      alert('Произошла ошибка при отправке заявки. Пожалуйста, позвоните нам по телефону +7 (708) 493-1267');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-black bg-opacity-50" onClick={onClose} />
        
        <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-secondary-800 shadow-xl rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {leadTypeLabels[leadType]}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-secondary-400 hover:text-gray-600 dark:hover:text-secondary-300 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {isSuccess ? (
            <div className="text-center py-8">
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Заявка отправлена!</h4>
              <p className="text-gray-600 dark:text-secondary-300 mb-4">
                Ваша заявка отправлена в WhatsApp. Наш менеджер свяжется с вами в течение 30 минут.
              </p>
              <div className="text-sm text-gray-500 dark:text-secondary-400">
                Заявка №{Date.now().toString().slice(-6)}
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {productName && (
                <div className="bg-gray-50 dark:bg-secondary-700 p-3 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-secondary-300">Товар:</p>
                  <p className="font-medium text-gray-900 dark:text-white">{productName}</p>
                  {calculatedPrice && (
                    <p className="text-sm text-primary-600">
                      Расчетная стоимость: {calculatedPrice.toLocaleString()} тг
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-1">
                  Ваше имя *
                </label>
                <input
                  type="text"
                  required
                  value={leadData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-secondary-400"
                  placeholder="Введите ваше имя"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-1">
                  Телефон *
                </label>
                <input
                  type="tel"
                  required
                  value={leadData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-secondary-400"
                  placeholder="+7 (___) ___-__-__"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={leadData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-secondary-400"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-1">
                  Комментарий
                </label>
                <textarea
                  value={leadData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-secondary-400"
                  placeholder="Дополнительная информация..."
                />
              </div>

              {/* Согласие на обработку персональных данных */}
              <div className="pt-4">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={leadData.agreeToDataProcessing}
                    onChange={(e) => handleInputChange('agreeToDataProcessing', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
                    required
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-secondary-300">
                    Я согласен(а) на обработку персональных данных в соответствии с{' '}
                    <a href="/privacy-policy" className="text-primary-600 hover:text-primary-700 underline">
                      политикой конфиденциальности
                    </a>
                    *
                  </span>
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 text-gray-700 dark:text-secondary-300 bg-gray-200 dark:bg-secondary-600 rounded-md hover:bg-gray-300 dark:hover:bg-secondary-500 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !leadData.agreeToDataProcessing}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadForm;